import { blendHexWithOverlay, fontDisplay, hexToRgbA, StylesIndexInjections } from 'common-react';
import { createStyles, makeUiStyles, Theme } from '../styles/styles';
import { focusStyle } from '../styles/mixins';

export interface CollapseButtonStyleProps {
  expanded: boolean;
  size?: 'medium';
  disableInteractionStyles?: boolean;
}

const getFontSize = (theme: Theme, size: CollapseButtonStyleProps['size']): string => {
  if (size === 'medium') {
    return fontDisplay('normal', theme.fontSize.base, 24);
  }
  return fontDisplay('normal', theme.fontSize.s, 20);
};

export default makeUiStyles<Theme, CollapseButtonStyleProps, 'root' | 'underline' | 'icon'>((theme: Theme) => {
  const textColor = theme.color.greys[80];
  const overlayColor = theme.color.greys[80];
  const hoverOpacity = 0.08;
  const pressedOpacity = 0.16;
  const hoverTextColor = blendHexWithOverlay(textColor, overlayColor, hoverOpacity);
  const pressedTextColor = blendHexWithOverlay(textColor, overlayColor, pressedOpacity);

  return createStyles({
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      margin: 0,
      padding: 0,
      border: 'none',
      backgroundColor: 'transparent',
      color: textColor,
      font: ({ size }: CollapseButtonStyleProps) => getFontSize(theme, size),
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'color 0.15s ease, background-color 0.15s ease',

      '&:hover': {
        color: hoverTextColor,
        backgroundColor: ({ disableInteractionStyles }: CollapseButtonStyleProps) => disableInteractionStyles ? 'transparent' : hexToRgbA(overlayColor, hoverOpacity),
      },

      '&:active, &:hover:active': {
        color: pressedTextColor,
        backgroundColor: ({ disableInteractionStyles }: CollapseButtonStyleProps) => disableInteractionStyles ? 'transparent' : hexToRgbA(overlayColor, pressedOpacity),
      },

      '&:focus': {
        outline: 'none',
      },

      '&:focus-visible': {
        ...focusStyle(theme),
      },

      '&:disabled': {
        pointerEvents: 'none',
        opacity: 0.4,
      },
    },
    underline: {
      textDecoration: 'underline',
      textDecorationColor: theme.color.violets[100],
      textDecorationStyle: 'solid',
      textDecorationSkipInk: 'none',
    },
    icon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      flexShrink: 0,
      transform: ({ expanded }: CollapseButtonStyleProps) => expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.18s ease-out',
      color: 'inherit',
      '& path': {
        fill: 'currentColor',
      },
    },
  });
}, { name: 'CollapseButton', index: StylesIndexInjections.UI_ATOMS });
